import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Minus, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { listTags } from "@/services/tagsApi";
import type { Tag } from "@/types/api";

/**
 * Diff panel for a PPPC `tagged` activity entry.
 *
 * Compares the deployment scope before (`previousTagIds`) and after
 * (`tagIds`) the tag-reconciliation event and surfaces three buckets:
 *   - **Added** tags now bound to the policy
 *   - **Removed** tags no longer bound
 *   - **Unchanged** tags carried over (rendered muted, optional)
 *
 * Tag names are resolved via the shared `listTags()` cache when available;
 * if the network is unavailable or a tag has been deleted since the event
 * was recorded, the raw id is shown instead so the diff stays useful in
 * read-only / offline contexts.
 */

interface PppcTaggedDiffProps {
  previousTagIds?: string[];
  tagIds?: string[];
  /** Render unchanged tags below the diff. Defaults to false (compact). */
  showUnchanged?: boolean;
  /** Tighter spacing inside dense timeline rows. */
  compact?: boolean;
}

interface DiffBuckets {
  added: string[];
  removed: string[];
  unchanged: string[];
}

export function diffTagSets(
  previous: string[] = [],
  next: string[] = [],
): DiffBuckets {
  const prev = new Set(previous);
  const nxt = new Set(next);
  const added: string[] = [];
  const removed: string[] = [];
  const unchanged: string[] = [];
  for (const id of nxt) {
    if (prev.has(id)) unchanged.push(id);
    else added.push(id);
  }
  for (const id of prev) {
    if (!nxt.has(id)) removed.push(id);
  }
  // Stable, deterministic order so snapshots don't churn.
  added.sort();
  removed.sort();
  unchanged.sort();
  return { added, removed, unchanged };
}

function tagId(t: Tag): string | undefined {
  return t.tagId || t.tag_id;
}
function tagName(t: Tag): string | undefined {
  return t.tagName || t.tag_name;
}

export function PppcTaggedDiff({
  previousTagIds,
  tagIds,
  showUnchanged = false,
  compact = false,
}: PppcTaggedDiffProps) {
  const diff = useMemo(
    () => diffTagSets(previousTagIds, tagIds),
    [previousTagIds, tagIds],
  );

  // Resolve names lazily — skip the network entirely when there's nothing
  // to render (e.g. an idempotent reconcile that produced an empty diff).
  const hasAnyTags =
    diff.added.length + diff.removed.length + diff.unchanged.length > 0;

  const { data: tags } = useQuery({
    queryKey: ["tags", "for-diff"],
    queryFn: listTags,
    enabled: hasAnyTags,
    staleTime: 1000 * 60 * 5,
  });

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tags ?? []) {
      const id = tagId(t);
      const name = tagName(t);
      if (id && name) map.set(id, name);
    }
    return map;
  }, [tags]);

  const label = (id: string) => nameById.get(id) ?? id;

  if (!hasAnyTags) return null;

  // No-op reconcile (added.length === 0 && removed.length === 0). We only
  // render this case when explicitly asked to show unchanged tags, since
  // the timeline already labels the row "Tags changed".
  const hasChange = diff.added.length > 0 || diff.removed.length > 0;
  if (!hasChange && !showUnchanged) return null;

  const prevCount = (previousTagIds ?? []).length;
  const nextCount = (tagIds ?? []).length;
  const gap = compact ? "gap-1" : "gap-1.5";
  const badgeClass = compact ? "text-[11px] px-1.5 py-0" : "text-xs";

  return (
    <div
      className={
        compact
          ? "mt-1.5 rounded-md border bg-background/60 p-2 space-y-1.5"
          : "mt-2 rounded-md border bg-background/60 p-3 space-y-2"
      }
      aria-label="Tag scope changes"
    >
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Scope</span>
        <span>{prevCount} tag{prevCount === 1 ? "" : "s"}</span>
        <ArrowRight className="h-3 w-3" aria-hidden="true" />
        <span>{nextCount} tag{nextCount === 1 ? "" : "s"}</span>
        {hasChange && (
          <span className="ml-auto inline-flex items-center gap-2">
            {diff.added.length > 0 && (
              <span className="inline-flex items-center gap-0.5 text-success">
                <Plus className="h-3 w-3" aria-hidden="true" />
                {diff.added.length}
              </span>
            )}
            {diff.removed.length > 0 && (
              <span className="inline-flex items-center gap-0.5 text-destructive">
                <Minus className="h-3 w-3" aria-hidden="true" />
                {diff.removed.length}
              </span>
            )}
          </span>
        )}
      </div>

      {diff.added.length > 0 && (
        <div className={`flex flex-wrap items-center ${gap}`}>
          <span className="text-[11px] uppercase tracking-wide text-success font-medium">
            Added
          </span>
          {diff.added.map((id) => (
            <Badge
              key={`add-${id}`}
              variant="outline"
              className={`${badgeClass} border-success/40 bg-success-muted text-success`}
            >
              <Plus className="mr-1 h-3 w-3" aria-hidden="true" />
              {label(id)}
            </Badge>
          ))}
        </div>
      )}

      {diff.removed.length > 0 && (
        <div className={`flex flex-wrap items-center ${gap}`}>
          <span className="text-[11px] uppercase tracking-wide text-destructive font-medium">
            Removed
          </span>
          {diff.removed.map((id) => (
            <Badge
              key={`rm-${id}`}
              variant="outline"
              className={`${badgeClass} border-destructive/40 bg-destructive/10 text-destructive line-through`}
            >
              <Minus className="mr-1 h-3 w-3" aria-hidden="true" />
              {label(id)}
            </Badge>
          ))}
        </div>
      )}

      {showUnchanged && diff.unchanged.length > 0 && (
        <div className={`flex flex-wrap items-center ${gap}`}>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            Unchanged
          </span>
          {diff.unchanged.map((id) => (
            <Badge
              key={`keep-${id}`}
              variant="outline"
              className={`${badgeClass} text-muted-foreground`}
            >
              {label(id)}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export default PppcTaggedDiff;
